import { FlashList } from '@shopify/flash-list';
import { Field, useFormikContext } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native';

import Highlighter from '@components/HighlightWords/index';
import { ProjectChecklistCenterModal } from '@components/ProjectDetail/ProjectChecklist/ProjectChecklistCenterModal';
import { Box, Text } from '@components/Restyle/index';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { IconSizes } from '@components/shared/Icon/RestyleIcon';
import SearchInput from '@components/shared/SearchInput/SearchInput';
import TextField from '@components/shared/TextField/TextField';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import {
  ListChecklistTemplatesNoItemsDocument,
  useListChecklistTemplatesNoItemsQuery,
  useSaveChecklistAsTemplateMutation,
  ChecklistTemplate,
  ListChecklistTemplatesDocument,
  useListProjectChecklistQuery,
} from '@graphql/generated';
import useMe from '@hooks/useMe';
import theme from '@themes/theme';

const SaveAsTemplateEmptyFooter: React.FC<{ isEmpty: boolean }> = ({
  isEmpty,
}) => {
  if (!isEmpty) return null;
  return (
    <Box height={52} justifyContent='center'>
      <Text variant='bodySecondaryNew' ellipsizeMode='tail' textAlign='left'>
        No templates found.
      </Text>
    </Box>
  );
};

export const ProjectSaveAsChecklistTemplate: React.FC = () => {
  const { t } = useTranslation();

  const {
    idProjectSaveChecklistAsTemplateOpen: projectUUId,
    setIdProjectSaveChecklistAsTemplateOpen,
  } = useWebDrawer();

  const [openSelectTemplateModal, setOpenSelectTemplateModal] = useState(false);
  // Template-specific data fetching
  const { data: listChecklistTemplatesNoItemsData } =
    useListChecklistTemplatesNoItemsQuery();
  const { values } = useFormikContext();
  const [searchVal, setSearchVal] = useState('');
  const [templateSelected, setTemplateSelected] = useState<
    ChecklistTemplate | undefined
  >(undefined);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const { me } = useMe();
  const [modalVisible, setModalVisible] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [saveChecklistAsTemplateMutation] =
    useSaveChecklistAsTemplateMutation();
  const { data: projectChecklistData } = useListProjectChecklistQuery({
    variables: {
      projectUUId,
    },
    skip: !projectUUId,
  });

  const templates =
    listChecklistTemplatesNoItemsData?.listChecklistTemplates ?? [];

  const filteredTemplates = templates.filter((t) => {
    const isOwner = t.owner.id === me?.id;
    if (!isOwner) return false;
    const keyword = searchVal.trim().toLowerCase();
    if (!keyword) return true;
    return t.templateName.toLowerCase().includes(keyword);
  });

  const submit = () => {
    const list =
      listChecklistTemplatesNoItemsData?.listChecklistTemplates.filter(
        (t) => t.owner.id === me?.id
      );
    if (
      list?.some(
        (t) => t.templateName === values.name && t.id !== templateSelected?.id
      )
    ) {
      setErrorMsg('Template name already exists.');
      setModalVisible(true);
      return;
    }
    if ((projectChecklistData?.listProjectChecklist?.length ?? 0) === 0) {
      setErrorMsg('No checklist items in this project.');
      setModalVisible(true);
      return;
    }
    const itemIdList = projectChecklistData?.listProjectChecklist.map(
      (t) => t.id
    );

    saveChecklistAsTemplateMutation({
      variables: {
        projectUuid: projectUUId,
        templateName: values.name,
        attributes: {
          templateId: templateSelected?.id,
          checklistItemIds: itemIdList,
        },
      },
      onCompleted: () => {
        setCreatingTemplate(false);
        setIdProjectSaveChecklistAsTemplateOpen(templateSelected ? '-2' : '-1');
      },
      refetchQueries: [
        ListChecklistTemplatesDocument,
        ListChecklistTemplatesNoItemsDocument,
      ],
    });
  };

  const templateIcon = (
    size: number,
    iconVariant: IconSizes,
    borderRadius: keyof typeof theme.borderRadii,
    marginRight: keyof typeof theme.spacing
  ) => {
    return (
      <Box
        width={size}
        height={size}
        alignItems='center'
        justifyContent='center'
        marginRight={marginRight}
        backgroundColor='blueMild'
        borderRadius={borderRadius}
        padding='xs'>
        <Icon name='FileText' variant={iconVariant} color='blue' />
      </Box>
    );
  };
  return (
    <>
      {modalVisible && (
        <ProjectChecklistCenterModal
          contentNode={
            <Box p='m'>
              <Box flexDirection='row' marginBottom='l' alignItems='center'>
                <Text
                  flex={1}
                  style={{ alignSelf: 'flex-start' }}
                  variant='labelLarge'
                  color='textPrimary'>
                  {t('models:projects.checklist.saveChecklistAsTemplate')}
                </Text>
                <TouchableOpacity
                  style={{ outline: 'none' }}
                  onPress={() => {
                    setModalVisible(false);
                  }}>
                  <Icon name='X' variant='m' />
                </TouchableOpacity>
              </Box>

              <Text
                marginBottom='m'
                style={{ alignSelf: 'flex-start' }}
                variant='webBodySecondary'
                color='textPrimary'>
                {errorMsg}
              </Text>

              <TouchableOpacity
                onPress={() => {
                  setModalVisible(false);
                }}>
                <Text
                  style={{ alignSelf: 'flex-end' }}
                  variant='buttonLabel'
                  color='greenSecondary'>
                  {t('models:projects.checklist.gotIt')}
                </Text>
              </TouchableOpacity>
            </Box>
          }
          onClose={() => {
            setModalVisible(false);
          }}
          visible={modalVisible}
        />
      )}
      {openSelectTemplateModal && (
        <ProjectChecklistCenterModal
          padding='none'
          width={504}
          onClose={() => {
            setOpenSelectTemplateModal(false);
          }}
          visible={openSelectTemplateModal}
          contentNode={
            <Box p='l' backgroundColor='white' height={432}>
              <Box flexDirection='row' mb='l' alignItems='center'>
                <Text
                  flex={1}
                  style={{ alignSelf: 'flex-start' }}
                  variant='labelLarge'
                  color='textPrimary'>
                  {t('models:projects.checklist.selectChecklist')}
                </Text>
                <Icon
                  name='X'
                  variant='m'
                  onPress={() => {
                    setOpenSelectTemplateModal(false);
                  }}
                />
              </Box>
              <Box flexDirection='row' alignItems='center' marginBottom='xs'>
                <SearchInput
                  onTextChanged={(newText) => setSearchVal(newText)}
                  value={searchVal}
                />
              </Box>
              <FlashList
                showsVerticalScrollIndicator={false}
                showsHorizontalScrollIndicator={false}
                ListFooterComponent={
                  <SaveAsTemplateEmptyFooter
                    isEmpty={filteredTemplates.length === 0}
                  />
                }
                data={filteredTemplates}
                estimatedItemSize={72}
                renderItem={({ item }) => (
                  <Box
                    height={72}
                    flexDirection='row'
                    borderBottomColor='grey02'
                    borderBottomWidth={1}
                    opacity={1}
                    alignItems='center'>
                    <Box
                      width={456}
                      flexDirection='row'
                      alignItems='center'
                      key={item.id}>
                      <TouchableOpacity
                        onPress={() => {
                          setTemplateSelected(item);
                          setOpenSelectTemplateModal(false);
                        }}>
                        {templateIcon(40, 'l', 'xs', 'xs')}
                      </TouchableOpacity>
                      <TouchableOpacity
                        onPress={() => {
                          setTemplateSelected(item);
                          setOpenSelectTemplateModal(false);
                        }}>
                        <Text
                          variant='bodySecondary'
                          color='textPrimary'
                          numberOfLines={2}
                          ellipsizeMode='tail'
                          textAlign='left'
                          style={{
                            lineHeight: 16,
                            maxWidth: 408,
                          }}>
                          <Highlighter
                            autoEscape
                            highlightStyle={{
                              color: theme.colors.textPrimary,
                              backgroundColor: theme.colors.yellowBright,
                            }}
                            searchWords={searchVal.split(' ')}
                            textToHighlight={item.templateName}
                          />
                        </Text>
                      </TouchableOpacity>
                    </Box>
                  </Box>
                )}
              />
            </Box>
          }></ProjectChecklistCenterModal>
      )}
      <Box flex={1} backgroundColor='white' m='l'>
        <Box flexDirection='row' mb='s' height={32}>
          <Text mr='m' variant='heading2' color='textPrimary'>
            {t('models:projects.checklist.saveChecklistAsTemplate')}
          </Text>

          <Box
            alignItems='flex-end'
            flexDirection='row'
            accessibilityLabel='Close'
            style={{ flex: 1, justifyContent: 'flex-end' }}>
            <Icon
              name='X'
              variant='l'
              onPress={() => {
                setIdProjectSaveChecklistAsTemplateOpen('');
              }}
            />
          </Box>
        </Box>

        <Field
          component={TextField}
          editable={!creatingTemplate}
          label='Name'
          accessibilityLabel='Name'
          name='name'
          isRequired
          placeholder={t('models:projects.checklist.enterTemplateName1')}
          showCharsRemaining
          textInputProps={{ maxLength: 50 }}
          marginBottom='l'
        />

        <Text mb='xs' variant='labelSmall' color='textPrimary'>
          {t('models:projects.checklist.template')}
          <Text variant='labelSmall' color='grey04'>
            {t('models:projects.checklist.ifYouLikeToUpdateTheTemplate')}
          </Text>
        </Text>

        <Button
          variant='edit'
          fullWidth
          onPress={() => {
            setOpenSelectTemplateModal(true);
          }}
          isSmall
          disabled={creatingTemplate}
          prefix={templateSelected && templateIcon(24, 'xxs', 'xxs', 'm')}
          accessibilityLabel={t('models:projects.checklist.template')}>
          {templateSelected && (
            <Text color='black' variant='labelSmall'>
              {templateSelected.templateName}
            </Text>
          )}
          {!templateSelected && (
            <Text color='grey04' variant='labelSmall'>
              {t('models:projects.checklist.template')}
            </Text>
          )}
        </Button>

        <Box flex={1}></Box>

        <Button
          marginTop='l'
          disabled={values.name.length === 0 || creatingTemplate}
          accessibilityLabel={t('models:projects.checklist.createTemplate')}
          backgroundColor={values.name.length > 0 ? 'black' : 'grey02'}
          fullWidth
          height={48}
          onPress={() => {
            submit();
          }}>
          {creatingTemplate && (
            <Box height={40}>
              <ActivityIndicatorLoading color='black' />
            </Box>
          )}

          {!creatingTemplate && (
            <Text
              variant='labelSmall'
              textAlign='center'
              color={values.name.length > 0 ? 'white' : 'grey04'}>
              {t('models:projects.checklist.createTemplate')}
            </Text>
          )}
        </Button>
      </Box>
    </>
  );
};
